/**
 * Application store: everything that reads or writes persisted data.
 *
 * Views call into here and never touch IndexedDB directly. Settings are cached
 * in memory because every view needs them (workstream lists, default location).
 */

import * as db from './db.js';
import { STORE_DAYS, STORE_META, STORE_SETTINGS } from './db.js';
import { addDays, firstOfMonth, lastOfMonth, monthOf, today } from './dates.js';
import { cleanDay, defaultSettings, emptyDay, isDayEmpty, normaliseDay, normaliseSettings, uid } from './model.js';
import { backupReminderDays, getStoragePersistence, invalidateStoragePersistence } from './persist.js';

let settingsCache = null;

// --- Settings ---------------------------------------------------------------

export async function getSettings() {
  if (settingsCache) return settingsCache;
  const stored = await db.get(STORE_SETTINGS, 'settings');
  settingsCache = normaliseSettings(stored);
  return settingsCache;
}

export async function saveSettings(settings) {
  const next = normaliseSettings(settings);
  await db.put(STORE_SETTINGS, next);
  settingsCache = next;
  return next;
}

/** Drop the in-memory copy, e.g. after a restore or a wipe. */
export function invalidateSettings() {
  settingsCache = null;
}

// --- Days -------------------------------------------------------------------

export async function getDay(date) {
  const settings = await getSettings();
  const raw = await db.get(STORE_DAYS, date);
  return raw ? normaliseDay(raw, settings.defaultLocation) : null;
}

/** The day record for `date`, or a blank one in memory if none exists yet. */
export async function getDayOrBlank(date) {
  const existing = await getDay(date);
  if (existing) return existing;
  const settings = await getSettings();
  return emptyDay(date, settings.defaultLocation);
}

/**
 * Save a day, stripping blank rows first.
 *
 * A day that holds nothing is deleted rather than stored, so simply paging
 * through the calendar never litters the database with empty records.
 */
export async function saveDay(day) {
  const clean = cleanDay(day);
  if (isDayEmpty(clean)) {
    await db.del(STORE_DAYS, clean.date);
    return clean;
  }
  await db.put(STORE_DAYS, clean);
  return clean;
}

export async function deleteDay(date) {
  await db.del(STORE_DAYS, date);
}

export async function getDaysBetween(from, to) {
  const settings = await getSettings();
  const rows = await db.getRange(STORE_DAYS, from, to);
  return rows.map((r) => normaliseDay(r, settings.defaultLocation)).filter(Boolean);
}

export async function getDaysInMonth(monthKey) {
  return getDaysBetween(firstOfMonth(monthKey), lastOfMonth(monthKey));
}

export async function getAllDays() {
  const settings = await getSettings();
  const rows = await db.getAll(STORE_DAYS);
  return rows
    .map((r) => normaliseDay(r, settings.defaultLocation))
    .filter(Boolean)
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** The most recent day record strictly before `date`, or null. */
export async function previousDay(date) {
  const settings = await getSettings();
  const rows = await db.getRange(STORE_DAYS, '0000-00-00', addDays(date, -1));
  if (!rows.length) return null;
  return normaliseDay(rows[rows.length - 1], settings.defaultLocation);
}

/**
 * Make sure a record exists for `date`, carrying work forward from the previous
 * recorded day:
 *   - planned items that were not ticked off become today's planned items, each
 *     marked `carried` so they read differently in the list;
 *   - waiting-on items that are still open are copied across, keeping their id
 *     and original start date so the "days open" count keeps running.
 *
 * Returns { day, created }.
 */
export async function ensureDay(date) {
  const existing = await getDay(date);
  if (existing) return { day: existing, created: false };

  const settings = await getSettings();
  const day = emptyDay(date, settings.defaultLocation);
  const previous = await previousDay(date);

  if (previous) {
    day.planned = previous.planned
      .filter((entry) => !entry.done && entry.text.trim())
      .map((entry) => ({
        id: uid(),
        text: entry.text,
        workstream: entry.workstream,
        done: false,
        carried: true,
      }));
    day.waitingOn = previous.waitingOn
      .filter((item) => !item.clearedDate && (item.who.trim() || item.what.trim()))
      .map((item) => ({ ...item }));
  }

  // Only persist if the carry-forward actually produced something; an untouched
  // blank day is not worth a record.
  if (day.planned.length || day.waitingOn.length) {
    await db.put(STORE_DAYS, day);
  }
  return { day, created: true };
}

// --- Meta (last backup, and anything else small) ----------------------------

export async function getMeta(key, fallback = null) {
  const row = await db.get(STORE_META, key);
  return row ? row.value : fallback;
}

export async function setMeta(key, value) {
  await db.put(STORE_META, { key, value });
}

export async function getAllMeta() {
  const rows = await db.getAll(STORE_META);
  return rows;
}

export const LAST_BACKUP_KEY = 'lastBackupAt';

export async function markBackupTaken(when = new Date().toISOString()) {
  await setMeta(LAST_BACKUP_KEY, when);
}

/**
 * Whether to nudge about backups: never backed up (but there is data to lose),
 * or the last backup is older than the reminder window.
 *
 * The window is 7 days when the browser has granted persistent storage, and 3
 * when it has not — without that guarantee the data could be evicted, so a
 * week between backups is too long to leave it.
 */
export async function backupReminder() {
  const dayCount = await db.count(STORE_DAYS);
  if (dayCount === 0) return null;

  const { persisted } = await getStoragePersistence();
  const threshold = backupReminderDays(persisted);

  const last = await getMeta(LAST_BACKUP_KEY, null);
  if (!last) return { never: true, days: null, dayCount, persisted, threshold };

  const days = Math.floor((Date.now() - new Date(last).getTime()) / 86400000);
  if (days > threshold) return { never: false, days, dayCount, last, persisted, threshold };
  return null;
}

// --- Whole-database operations ----------------------------------------------

/** Everything, in the shape used by the JSON backup file. */
export async function exportAll() {
  const [settings, days, meta] = await Promise.all([getSettings(), getAllDays(), getAllMeta()]);
  return {
    app: 'timesheet',
    formatVersion: 1,
    exportedAt: new Date().toISOString(),
    settings,
    days,
    meta: meta.filter((m) => m.key !== LAST_BACKUP_KEY),
  };
}

/**
 * Import a backup.
 *
 * mode 'replace' wipes existing days and settings first; mode 'merge' keeps the
 * current settings and only overwrites days whose dates appear in the file.
 */
export async function importAll(payload, mode = 'merge') {
  const days = (payload.days || [])
    .map((d) => normaliseDay(d, payload?.settings?.defaultLocation || 'Bridgend'))
    .filter(Boolean);

  if (mode === 'replace') {
    await db.replaceAll(STORE_DAYS, days);
    if (payload.settings) await saveSettings(payload.settings);
    else {
      await saveSettings(defaultSettings());
    }
  } else {
    await db.putMany(STORE_DAYS, days);
  }

  if (Array.isArray(payload.meta) && payload.meta.length) {
    await db.putMany(STORE_META, payload.meta.filter((m) => m && m.key));
  }
  invalidateSettings();
  return days.length;
}

/** Summary used by the restore confirmation step, before anything is written. */
export async function describeImport(payload) {
  const incoming = (payload.days || []).filter((d) => d && d.date);
  const dates = new Set(incoming.map((d) => d.date));
  const existing = await getAllDays();
  const existingDates = new Set(existing.map((d) => d.date));
  let overlapping = 0;
  for (const date of dates) if (existingDates.has(date)) overlapping += 1;
  return {
    incomingDays: dates.size,
    existingDays: existingDates.size,
    overlapping,
    hasSettings: Boolean(payload.settings),
    exportedAt: payload.exportedAt || null,
  };
}

export async function clearAllData() {
  await db.deleteDatabase();
  invalidateSettings();
  invalidateStoragePersistence();
}

/** Convenience for the Today view's first load. */
export async function todayRecord() {
  return ensureDay(today());
}

export { today, monthOf };
